/**
 * Build Agg System Summary with flexible 2-level drilldown
 *
 * Production values are summed from filtered wells.
 * Permits use system-level values (once per system+year).
 */

import { computePctPermit, compareAggSystems } from "../utils";
import {
  createEmptyTotals,
  accumulateRow,
  accumulateWellDetailRow,
} from "./buildTotals";

/**
 * Build aggregate system tree with configurable drilldown
 * @param {import("../types").ProductionRow[]} filteredRows - Filtered production rows
 * @param {import("../types").DrillMode} mode - Drilldown mode ("year-wells" or "wells-year")
 * @returns {import("../types").AggSystemSummary} Agg system summary with grand totals
 */
export const buildAggSystemTree = (filteredRows, mode) => {
  const aggSystemMap = new Map();
  const grandTotals = createEmptyTotals();
  const grandSeenSystems = new Set();

  for (const row of filteredRows) {
    const aggKey = row.agg_system_ndx;
    const aggName = row.agg_system_name || "Unknown";
    const year = row.report_year;
    const wellKey = row.well_ndx;
    const wellNumber = row.cuwcd_well_number || "";
    const wellName = row.well_name || "";

    if (!aggSystemMap.has(aggKey)) {
      aggSystemMap.set(aggKey, {
        agg_system_ndx: aggKey,
        agg_system_name: aggName,
        totals: createEmptyTotals(),
        seenSystems: new Set(),
        children: new Map(),
      });
    }

    const aggGroup = aggSystemMap.get(aggKey);

    // Accumulate filtered wells' production, permits once per system
    accumulateWellDetailRow(aggGroup.totals, row, aggGroup.seenSystems);

    if (mode === "year-wells") {
      if (!aggGroup.children.has(year)) {
        aggGroup.children.set(year, {
          year,
          totals: createEmptyTotals(),
          seenSystems: new Set(),
          wells: new Map(),
        });
      }

      const yearGroup = aggGroup.children.get(year);

      // Accumulate filtered wells' production for this year
      accumulateWellDetailRow(yearGroup.totals, row, yearGroup.seenSystems);

      if (!yearGroup.wells.has(wellKey)) {
        yearGroup.wells.set(wellKey, {
          well_ndx: wellKey,
          cuwcd_well_number: wellNumber,
          well_name: wellName,
          totals: createEmptyTotals(),
        });
      }

      // Individual well uses per-well values
      accumulateRow(yearGroup.wells.get(wellKey).totals, row);
    } else {
      // wells-year mode
      if (!aggGroup.children.has(wellKey)) {
        aggGroup.children.set(wellKey, {
          well_ndx: wellKey,
          cuwcd_well_number: wellNumber,
          well_name: wellName,
          totals: createEmptyTotals(),
          years: new Map(),
        });
      }

      const wellGroup = aggGroup.children.get(wellKey);

      if (!wellGroup.years.has(year)) {
        wellGroup.years.set(year, {
          year,
          totals: createEmptyTotals(),
        });
      }

      // Individual well/year uses per-well values
      accumulateRow(wellGroup.years.get(year).totals, row);
      accumulateRow(wellGroup.totals, row);
    }

    // Grand totals: sum filtered wells' production, permits once per system
    accumulateWellDetailRow(grandTotals, row, grandSeenSystems);
  }

  const aggSystems = Array.from(aggSystemMap.values())
    .map((agg) => {
      let children;

      if (mode === "year-wells") {
        children = Array.from(agg.children.values())
          .map((yearGroup) => ({
            ...yearGroup,
            pct_permit: computePctPermit(yearGroup.totals),
            wells: Array.from(yearGroup.wells.values())
              .map((w) => ({
                ...w,
                pct_permit: computePctPermit(w.totals),
              }))
              .sort((a, b) =>
                (a.cuwcd_well_number || "").localeCompare(
                  b.cuwcd_well_number || ""
                )
              ),
          }))
          .sort((a, b) => b.year - a.year);
      } else {
        children = Array.from(agg.children.values())
          .map((wellGroup) => ({
            ...wellGroup,
            pct_permit: computePctPermit(wellGroup.totals),
            years: Array.from(wellGroup.years.values())
              .map((y) => ({
                ...y,
                pct_permit: computePctPermit(y.totals),
              }))
              .sort((a, b) => b.year - a.year),
          }))
          .sort((a, b) =>
            (a.cuwcd_well_number || "").localeCompare(b.cuwcd_well_number || "")
          );
      }

      return {
        agg_system_ndx: agg.agg_system_ndx,
        agg_system_name: agg.agg_system_name,
        totals: agg.totals || createEmptyTotals(),
        pct_permit: computePctPermit(agg.totals || createEmptyTotals()),
        children,
      };
    })
    .sort((a, b) => compareAggSystems(a.agg_system_name, b.agg_system_name));

  return {
    aggSystems,
    grandTotals: {
      ...grandTotals,
      pct_permit: computePctPermit(grandTotals),
    },
  };
};
